import { useEffect, useRef, useState, useCallback } from 'react';
import { C } from '../styles/tokens.js';

// pdfjs-dist 5.x: ESM 진입점 + worker는 url import 권장
import * as pdfjsLib from 'pdfjs-dist';
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

/**
 * 실제 PDF 렌더링 모달.
 * 우선순위: file.url > file.blob > 데모 모드(샘플 PDF 데이터 URL).
 *   - file.url: SharePoint 다운로드 URL 등
 *   - file.blob: 메모리 상의 Blob (재업로드 직후 등)
 *   - 둘 다 없으면 미리보기 placeholder 표시
 */
export default function PdfViewerModal({ file, onClose }) {
  const [pdf, setPdf] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageText, setPageText] = useState('');
  const [copied, setCopied] = useState(false);
  const [thumbs, setThumbs] = useState([]);
  const canvasRef = useRef(null);

  // 1) 문서 로드
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const src = file.url ? { url: file.url }
                  : file.blob ? { data: await file.blob.arrayBuffer() }
                  : null;
        if (!src) {
          if (!cancelled) { setError('데모 모드: URL 미지정'); setLoading(false); }
          return;
        }
        const doc = await pdfjsLib.getDocument(src).promise;
        if (cancelled) return;
        setPdf(doc);
        setTotal(doc.numPages);
        setLoading(false);
      } catch (e) {
        if (!cancelled) { setError(e.message || 'PDF 로드 실패'); setLoading(false); }
      }
    })();

    return () => { cancelled = true; };
  }, [file]);

  // 2) 현재 페이지 렌더 + 텍스트 추출
  useEffect(() => {
    if (!pdf || !canvasRef.current) return;
    let cancelled = false;

    (async () => {
      try {
        const p = await pdf.getPage(page);
        if (cancelled) return;
        const viewport = p.getViewport({ scale: zoom * 1.5 });
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await p.render({ canvasContext: ctx, viewport }).promise;
        if (cancelled) return;

        const tc = await p.getTextContent();
        setPageText(tc.items.map(i => i.str).join(' '));
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();

    return () => { cancelled = true; };
  }, [pdf, page, zoom]);

  // 3) 썸네일 백그라운드 생성 (페이지당 1회)
  useEffect(() => {
    if (!pdf) return;
    let cancelled = false;

    (async () => {
      const out = [];
      for (let n = 1; n <= Math.min(pdf.numPages, 50); n++) {
        if (cancelled) return;
        try {
          const p = await pdf.getPage(n);
          const viewport = p.getViewport({ scale: 0.18 });
          const off = document.createElement('canvas');
          off.width = viewport.width;
          off.height = viewport.height;
          await p.render({ canvasContext: off.getContext('2d'), viewport }).promise;
          out.push({ n, src: off.toDataURL('image/png') });
          if (!cancelled) setThumbs([...out]);
        } catch (_) {}
      }
    })();

    return () => { cancelled = true; };
  }, [pdf]);

  const copyText = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(pageText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {}
  }, [pageText]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.surface, borderRadius: 12, width: 980, height: 680, border: `1px solid ${C.border}`,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 24px 64px rgba(0,0,0,0.7)', resize: 'both', minWidth: 600, minHeight: 400,
      }}>
        {/* 헤더 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 13, color: C.text, fontWeight: 700 }}>
            📄 {file.name}
            {error && <span style={{ marginLeft: 10, fontSize: 11, color: C.red }}>· {error}</span>}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button onClick={copyText} disabled={!pageText} style={{
              padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: pageText ? 'pointer' : 'not-allowed',
              border: `1px solid ${copied ? C.green + '44' : C.border}`,
              background: copied ? C.greenBg : C.surface2,
              color: copied ? C.green : C.dim, opacity: pageText ? 1 : 0.5,
            }}>{copied ? '✓ 복사됨' : '📋 텍스트 복사'}</button>
            <button onClick={() => setZoom(z => Math.max(z - 0.1, 0.5))} style={{ width: 26, height: 26, borderRadius: 4, border: `1px solid ${C.border}`, background: C.surface2, color: C.dim, cursor: 'pointer' }}>−</button>
            <span style={{ fontSize: 11, color: C.dim, minWidth: 40, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(z => Math.min(z + 0.1, 2.5))} style={{ width: 26, height: 26, borderRadius: 4, border: `1px solid ${C.border}`, background: C.surface2, color: C.dim, cursor: 'pointer' }}>+</button>
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: C.dim, fontSize: 22, cursor: 'pointer', marginLeft: 8 }}>×</button>
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* 썸네일 */}
          <div style={{ width: 130, background: C.surface2, borderRight: `1px solid ${C.border}`, overflowY: 'auto', padding: 10 }}>
            {thumbs.length === 0 && !error && (
              <div style={{ fontSize: 10, color: C.dim, padding: '20px 4px', textAlign: 'center' }}>썸네일 생성 중...</div>
            )}
            {thumbs.map(t => (
              <div key={t.n} onClick={() => setPage(t.n)} style={{
                background: page === t.n ? C.goldBg : C.surface,
                border: `1px solid ${page === t.n ? C.gold : C.border}`,
                borderRadius: 4, padding: 4, marginBottom: 6, cursor: 'pointer', textAlign: 'center',
              }}>
                <img src={t.src} alt={`p${t.n}`} style={{ width: '100%', display: 'block', borderRadius: 2 }} />
                <div style={{ fontSize: 9, color: page === t.n ? C.gold : C.dim, marginTop: 3 }}>p.{t.n}</div>
              </div>
            ))}
            {/* error 시 placeholder 썸네일 */}
            {error && Array.from({ length: 8 }, (_, i) => i + 1).map(n => (
              <div key={n} onClick={() => setPage(n)} style={{
                background: page === n ? C.goldBg : C.surface,
                border: `1px solid ${page === n ? C.gold : C.border}`,
                borderRadius: 4, padding: 10, marginBottom: 6, cursor: 'pointer',
                height: 70, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontSize: 18, color: C.dim }}>📄</span>
                <span style={{ fontSize: 9, color: C.dim, marginTop: 2 }}>p.{n}</span>
              </div>
            ))}
          </div>

          {/* 페이지 캔버스 */}
          <div style={{ flex: 1, overflow: 'auto', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            {loading && <div style={{ color: C.dim, fontSize: 14 }}>로딩 중...</div>}
            {error && !loading && (
              <div style={{ width: 480 * zoom, height: 620 * zoom, background: '#fff', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, color: '#333', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
                <div style={{ fontSize: 64 }}>📄</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>페이지 {page}</div>
                <div style={{ fontSize: 12, color: '#888' }}>{file.name}</div>
                <div style={{ fontSize: 11, color: '#aaa', padding: '8px 16px', background: '#f5f5f5', borderRadius: 4 }}>
                  file.url 또는 file.blob 지정 시 실 렌더링됩니다
                </div>
              </div>
            )}
            <canvas ref={canvasRef} style={{ display: pdf && !loading ? 'block' : 'none', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', borderRadius: 4 }} />
          </div>
        </div>

        {/* 푸터 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderTop: `1px solid ${C.border}`, background: C.surface2 }}>
          <button onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page <= 1} style={{
            padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: page <= 1 ? 'not-allowed' : 'pointer',
            border: `1px solid ${C.border}`, background: 'transparent', color: C.dim, opacity: page <= 1 ? 0.4 : 1,
          }}>◀ 이전</button>
          <div style={{ fontSize: 12, color: C.dim }}>
            {page} / {total || (error ? 8 : '?')} 페이지
          </div>
          <button onClick={() => setPage(p => Math.min(p + 1, total || 8))} disabled={page >= (total || 8)} style={{
            padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: page >= (total || 8) ? 'not-allowed' : 'pointer',
            border: `1px solid ${C.border}`, background: 'transparent', color: C.dim, opacity: page >= (total || 8) ? 0.4 : 1,
          }}>다음 ▶</button>
        </div>
      </div>
    </div>
  );
}
